import { AnimatedContainer, AnimatedSection } from '@components/animated'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'

import { useApplication } from '@services/applications/queries/useApplication'
import { useContracts } from '@services/contracts/queires/useContracts'
import { useOffers } from '@services/offers/queries/useOffers'
import { useProfile } from '@services/users/queries/useProfile'

import { ApplicationCard } from '../../components/user/ApplicationCard'
import { ContractSummaryCard } from '../../components/user/ContractSummaryCard'
import { OffersCard } from '../../components/user/OffersCard'
import { SetupProgressCard } from '../../components/user/SetupProgressCard'
import { TicketsCard } from '@features/dashboard/components/user/TicketsCard'

function greetingKey() {
  const hour = new Date().getHours()
  if (hour < 12) return 'morning'
  if (hour < 18) return 'afternoon'
  return 'evening'
}

export function UserDashboard() {
  const { t } = useTranslation('userDashboard')

  const { data: profile } = useProfile(true)
  const { data: application, isLoading: applicationLoading } = useApplication()
  const { data: offers, isLoading: offersLoading } = useOffers()
  const { data: contracts } = useContracts()

  const name = profile?.first_name || profile?.email?.split('@')[0] || ''
  const hasContract = !!profile?.has_contract

  return (
    <div className="app-chrome space-y-6">
      <motion.header
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="flex items-center gap-3"
      >
        <span
          aria-hidden
          className="h-9 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-primary to-peach"
        />
        <div>
          <h1 className="m-0 font-display text-2xl font-bold tracking-tight text-ink lg:text-3xl">
            {t(`greeting.${greetingKey()}`, { name })}
          </h1>
          <p className="mt-0.5 text-muted-foreground">
            {t('greeting.subtitle')}
          </p>
        </div>
      </motion.header>

      <AnimatedContainer staggerDelay={0.08}>
        <div className="grid items-stretch gap-5 md:grid-cols-2 lg:grid-cols-3">
          {hasContract && (
            <AnimatedSection className="md:col-span-2 lg:col-span-3">
              <ContractSummaryCard contract={contracts?.results?.[0]} />
            </AnimatedSection>
          )}

          <AnimatedSection className="h-full md:col-span-2 lg:col-span-2">
            <ApplicationCard
              application={application}
              loading={applicationLoading}
            />
          </AnimatedSection>

          <AnimatedSection className="h-full">
            <SetupProgressCard profile={profile} application={application} />
          </AnimatedSection>

          <AnimatedSection className="h-full md:col-span-2 lg:col-span-3">
            <OffersCard offers={offers} loading={offersLoading} />
          </AnimatedSection>

          <AnimatedSection className="h-full md:col-span-2 lg:col-span-3">
            <TicketsCard />
          </AnimatedSection>
        </div>
      </AnimatedContainer>
    </div>
  )
}
